/**
 * The judge-flagged appointments list for the admin panel: every visit the
 * capture judge flagged as suspect-uncaptured, missing presence entirely, or
 * incomplete, with the evidence it based that verdict on. Follows the admin
 * card-list split: the list component owns the empty state, the parent panel
 * owns the AsyncBoundary/skeleton.
 */
import { useSessionStore } from "@auth/sessionStore";
import type {
  CaptureEvidence,
  CaptureVerdict,
  EvidenceStay,
  EvidenceTranscript,
  FlaggedVisit,
} from "@capture/captureClient";
import { useFlaggedVisits } from "@capture/captureQueries";
import type { Room } from "@rooms/roomsClient";
import { useRoomsList } from "@rooms/roomsQueries";
import {
  Button,
  cn,
  EmptyNote,
  LabelTape,
  PaperPanel,
  Swipe,
  type SwipeTone,
} from "@scribenote/brian-design-system";
import { formatDateTime, formatTime } from "@utils/format-date";
import { relativeTime } from "@utils/relative-time";
import { useOpenVisit } from "@visits/useOpenVisit";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";

/**
 * The verdict said in a word under the highlighter that means it. A visit the
 * judge believes went unrecorded is the one that stopped something, so it takes
 * the stopped marker; the other two are things to go and look at, and take the
 * flag. Blue is never used here: it means "Brian did this" and nothing else.
 */
const VERDICT_META: Record<CaptureVerdict, { label: string; tone: SwipeTone }> =
  {
    suspect_uncaptured: { label: "Suspect uncaptured", tone: "stopped" },
    no_presence_recorded: { label: "No presence recorded", tone: "flag" },
    incomplete: { label: "Incomplete", tone: "flag" },
  };

/** The quiet inline disclosure control: an annotation you can click, not a
 * button competing with the evidence it reveals (the PimsAudit idiom). */
const disclosure =
  "text-xs font-medium text-accent-deep hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent";

/** The room a stay happened in: its label tape when the roster knows the room,
 * the bare uuid stem in mono when it doesn't, and nothing to label at all when
 * the stay names no room. */
const StayRoom = ({
  rooms,
  roomUuid,
}: {
  rooms: Room[];
  roomUuid: string | null;
}) => {
  if (!roomUuid) return <span className="text-annotation">Unknown room</span>;
  const name = rooms.find((r) => r.id === roomUuid)?.name;
  return name ? (
    <LabelTape className="max-w-[10rem] truncate align-middle">
      {name}
    </LabelTape>
  ) : (
    <span className="font-mono">{roomUuid.slice(0, 8)}</span>
  );
};

const scheduledLine = (visit: FlaggedVisit): string => {
  if (!visit.scheduledStart && !visit.scheduledEnd) return "Unscheduled";
  if (!visit.scheduledStart) return "Unscheduled";
  const start = formatDateTime(visit.scheduledStart);
  return visit.scheduledEnd
    ? `${start} – ${formatTime(visit.scheduledEnd)}`
    : start;
};

const evidenceSummary = (evidence: CaptureEvidence): ReactNode => {
  const hansTotal =
    evidence.hansDone + evidence.hansFailed + evidence.hansPending;
  const needsRecoveryCount = evidence.transcripts.filter(
    (t) => t.needsRecovery || (!t.subjectLinked && !t.roomOverlap),
  ).length;

  return (
    <span className="text-xs text-annotation">
      {evidence.stays.length} room stays &middot; {evidence.transcripts.length}{" "}
      transcripts &middot; {evidence.chunkMessageCount} chunks &middot;{" "}
      {evidence.memoCount} memos &middot; {evidence.factCount} facts &middot;{" "}
      <span
        className={evidence.hansFailed > 0 ? "text-ink-stopped" : undefined}
      >
        PIMS writes {evidence.hansDone}/{hansTotal}
      </span>
      {evidence.stagesComplete === false && (
        <span className="text-ink-stopped"> &middot; stages incomplete</span>
      )}
      {needsRecoveryCount > 0 && ` (${needsRecoveryCount} need recovery)`}
    </span>
  );
};

const StayRow = ({ stay, rooms }: { stay: EvidenceStay; rooms: Room[] }) => (
  <div className="flex flex-wrap items-center gap-x-2 text-xs text-annotation">
    <StayRoom rooms={rooms} roomUuid={stay.roomUuid} />
    <span className="font-mono">
      {formatTime(stay.enteredAt)}
      {" – "}
      {stay.leftAt ? formatTime(stay.leftAt) : "still in room"}
    </span>
  </div>
);

const TranscriptRow = ({ transcript }: { transcript: EvidenceTranscript }) => (
  <div className="text-xs text-annotation">
    <span className="font-mono">{transcript.transcriptUuid.slice(0, 8)}</span>{" "}
    &middot; {transcript.turnCount} turns &middot; started{" "}
    {formatTime(transcript.startedAt)}
    {!transcript.subjectLinked && " · unlinked"}
    {!transcript.roomOverlap && " · no room overlap"}
    {transcript.needsRecovery && (
      <span className="text-ink-stopped"> &middot; needs recovery</span>
    )}
  </div>
);

const FlaggedVisitCard = ({
  visit,
  rooms,
}: {
  visit: FlaggedVisit;
  rooms: Room[];
}) => {
  const openVisit = useOpenVisit();
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const reasoningRef = useRef<HTMLParagraphElement>(null);
  const [reasoningTruncated, setReasoningTruncated] = useState(false);
  const verdictMeta = VERDICT_META[visit.verdict];
  const confidencePct = Math.round(
    Math.min(Math.max(visit.confidence, 0), 1) * 100,
  );

  // Whether the clamp actually truncates depends on rendered width, not
  // character count, so measure the clamped paragraph (and re-measure on
  // resize). While expanded the paragraph is unclamped and would measure as
  // not-truncated, so keep the last collapsed measurement instead.
  useLayoutEffect(() => {
    if (reasoningExpanded) return;
    const el = reasoningRef.current;
    if (!el) return;
    const measure = () =>
      setReasoningTruncated(el.scrollHeight > el.clientHeight + 1);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [reasoningExpanded, visit.reasoning]);

  return (
    <PaperPanel
      className="p-4"
      data-testid={`flagged-visit-${visit.visitUuid}`}
    >
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <span className="min-w-0">
            <span className="font-display font-medium wrap-anywhere text-ink">
              {visit.patientName ?? "Unknown patient"}
            </span>{" "}
            <span className="text-sm text-annotation">{visit.visitType}</span>
          </span>
          <span className="flex shrink-0 items-center gap-3">
            <span className="font-mono text-xs text-annotation">
              {relativeTime(new Date(visit.judgedAt).getTime())}
            </span>
            <Button
              variant="outline"
              size="sm"
              data-testid={`flagged-open-visit-${visit.visitUuid}`}
              onClick={() =>
                openVisit({
                  visitUuid: visit.visitUuid,
                  patientUuid: visit.patientUuid,
                  patientName: visit.patientName,
                  scheduledStart: visit.scheduledStart,
                  currentRoomUuid: undefined,
                })
              }
            >
              Open visit
            </Button>
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <Swipe
            tone={verdictMeta.tone}
            data-testid={`verdict-badge-${visit.visitUuid}`}
          >
            {verdictMeta.label}
          </Swipe>
          <span className="font-mono text-annotation">
            {confidencePct}% confidence
          </span>
        </div>

        <div className="font-mono text-xs text-annotation">
          {scheduledLine(visit)}
        </div>

        <div className="flex flex-col items-start gap-1">
          <p
            ref={reasoningRef}
            className={cn(
              "text-sm text-ink-body",
              !reasoningExpanded && "line-clamp-3",
            )}
          >
            {visit.reasoning}
          </p>
          {(reasoningTruncated || reasoningExpanded) && (
            <button
              type="button"
              onClick={() => setReasoningExpanded((v) => !v)}
              aria-expanded={reasoningExpanded}
              className={disclosure}
            >
              {reasoningExpanded ? "Show less" : "Show more"}
            </button>
          )}
        </div>

        <div>{evidenceSummary(visit.evidence)}</div>

        <div className="flex flex-col items-start gap-2">
          <button
            type="button"
            onClick={() => setEvidenceExpanded((v) => !v)}
            aria-expanded={evidenceExpanded}
            data-testid={`flagged-evidence-toggle-${visit.visitUuid}`}
            className={disclosure}
          >
            {evidenceExpanded ? "Hide details" : "Details"}
          </button>
          {evidenceExpanded && (
            <div className="flex w-full flex-col gap-2 rounded-paper bg-recess px-3 py-2">
              {visit.evidence.stays.length > 0 && (
                <div className="flex flex-col gap-1">
                  {visit.evidence.stays.map((stay, i) => (
                    <StayRow
                      key={`${stay.roomUuid}-${i}`}
                      stay={stay}
                      rooms={rooms}
                    />
                  ))}
                </div>
              )}
              {visit.evidence.transcripts.length > 0 && (
                <div className="flex flex-col gap-1">
                  {visit.evidence.transcripts.map((transcript) => (
                    <TranscriptRow
                      key={transcript.transcriptUuid}
                      transcript={transcript}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </PaperPanel>
  );
};

export const FlaggedVisitsList = () => {
  const sessionKey = useSessionStore((s) => s.user?.userUuid ?? "");
  const { data: visits } = useFlaggedVisits(sessionKey);
  const { data: rooms } = useRoomsList(sessionKey);

  if (visits.length === 0)
    return <EmptyNote>Nothing I&rsquo;d flag.</EmptyNote>;

  return visits.map((v) => (
    <FlaggedVisitCard key={v.visitUuid} visit={v} rooms={rooms} />
  ));
};
